import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';
import { getUploadUrl, toUploadPath } from '../utils/uploadUrl';

function AdminDepartments() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [letterFilter, setLetterFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    alias: '',
    description: '',
    vision: '',
    mission: '',
    image: '',
    status: 'active',
    units: []
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  const navigate = useNavigate();

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchDepartments();
  }, [navigate, search, letterFilter]);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (letterFilter) params.append('letter', letterFilter);
      params.append('include_inactive', 'true');

      const response = await axios.get(`/api/departments?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setDepartments(response.data.departments || []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const token = getAccessToken();
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = toUploadPath(response.data.filename || response.data.path || response.data.imageUrl);
      setFormData(prev => ({ ...prev, image: imageUrl }));
      alert('Image uploaded successfully!');
    } catch (error) {
      alert('Failed to upload image');
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: '' }));
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
    alert('Image removed. Save the department to apply the change.');
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      alias: '',
      description: '',
      vision: '',
      mission: '',
      image: '',
      status: 'active',
      units: []
    });
    setFormError('');
    setShowAddModal(true);
  };

  const handleEdit = async (department) => {
    try {
      const token = getAccessToken();
      const response = await axios.get(`/api/departments/${department.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const fullDepartment = response.data;
      
      setEditingDepartment(fullDepartment);
      setFormData({
        name: fullDepartment.name || '',
        alias: fullDepartment.alias || '',
        description: fullDepartment.description || '',
        vision: fullDepartment.vision || '',
        mission: fullDepartment.mission || '',
        image: fullDepartment.image || '',
        status: fullDepartment.status || 'active',
        units: fullDepartment.units || []
      });
      setFormError('');
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching department details:', error);
      alert('Failed to load department details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      const token = getAccessToken();
      const data = {
        ...formData,
        units: formData.units.filter(unit => unit.name && unit.name.trim())
      };

      if (editingDepartment) {
        await axios.put(`/api/departments/${editingDepartment.id}`, data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Department updated successfully!');
      } else {
        await axios.post('/api/departments', data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Department created successfully!');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setEditingDepartment(null);
      fetchDepartments();
    } catch (error) {
      console.error('Error saving department:', error);
      if (error.response?.data?.error) {
        setFormError(error.response.data.error);
      } else if (error.message) {
        setFormError(error.message);
      } else {
        setFormError('Failed to save department. Please check the console for details.');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) {
      return;
    }

    try {
      const token = getAccessToken();
      await axios.delete(`/api/departments/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      alert('Department deleted successfully!');
      fetchDepartments();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete department');
      console.error('Error deleting department:', error);
    }
  };

  const addUnit = () => {
    setFormData(prev => ({
      ...prev,
      units: [...prev.units, { name: '', description: '', display_order: prev.units.length, status: 'active' }]
    }));
  };

  const removeUnit = (index) => {
    setFormData(prev => ({
      ...prev,
      units: prev.units.filter((_, i) => i !== index)
    }));
  };

  const updateUnit = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      units: prev.units.map((unit, i) => 
        i === index ? { ...unit, [field]: value } : unit
      )
    }));
  };

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>Departments Management</h1>
          <button className="btn btn-primary" onClick={handleAdd}>
            Add Department
          </button>
        </div>

        {/* Filters */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search departments..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <select
                  className="form-select"
                  value={letterFilter}
                  onChange={(e) => setLetterFilter(e.target.value)}
                >
                  <option value="">All Letters</option>
                  {alphabet.map(letter => (
                    <option key={letter} value={letter}>{letter}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Departments List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Letter</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center">No departments found</td>
                      </tr>
                    ) : (
                      departments.map((dept) => (
                        <tr key={dept.id}>
                          <td>{dept.name}</td>
                          <td>{dept.first_letter}</td>
                          <td>
                            <span className={`badge ${dept.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                              {dept.status}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-primary me-2"
                              onClick={() => handleEdit(dept)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(dept.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        {(showAddModal || showEditModal) && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ margin: '1.75rem auto', maxWidth: '800px' }}>
              <div className="modal-content">
                <div className="modal-header" style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1, borderBottom: '1px solid #dee2e6' }}>
                  <h5 className="modal-title">
                    {editingDepartment ? 'Edit Department' : 'Add Department'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setEditingDepartment(null);
                      setFormError('');
                    }}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                    {formError && (
                      <div className="alert alert-danger">{formError}</div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Alias</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.alias}
                        onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Vision</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.vision}
                        onChange={(e) => setFormData({ ...formData, vision: e.target.value })}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Mission</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.mission}
                        onChange={(e) => setFormData({ ...formData, mission: e.target.value })}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Image</label>
                      <input
                        ref={imageInputRef}
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && <div className="text-muted mt-2">Uploading...</div>}
                      {formData.image && (
                        <div className="mt-2">
                          <img
                            src={getUploadUrl(formData.image)}
                            alt="Preview"
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', borderRadius: '4px' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="mt-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={removeImage}
                            >
                              <i className="bi bi-trash me-1"></i>
                              Remove Image
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    {/* Units Section */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label mb-0">Units</label>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={addUnit}
                        >
                          Add Unit
                        </button>
                      </div>
                      {formData.units.map((unit, index) => (
                        <div key={index} className="card mb-2">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="mb-0">Unit {index + 1}</h6>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => removeUnit(index)}
                              >
                                Remove
                              </button>
                            </div>
                            <div className="mb-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Unit Name *"
                                value={unit.name}
                                onChange={(e) => updateUnit(index, 'name', e.target.value)}
                                required
                              />
                            </div>
                            <div>
                              <textarea
                                className="form-control"
                                rows="2"
                                placeholder="Unit Description"
                                value={unit.description || ''}
                                onChange={(e) => updateUnit(index, 'description', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="modal-footer" style={{ position: 'sticky', bottom: 0, backgroundColor: '#fff', zIndex: 1, borderTop: '1px solid #dee2e6' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAddModal(false);
                        setShowEditModal(false);
                        setEditingDepartment(null);
                        setFormError('');
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={formLoading}>
                      {formLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminDepartments;
